import { type ChangeEvent, type ReactNode, useEffect, useState } from 'react';
import { InputAdornment } from '@material-ui/core';
import FormTextInput from 'components/FormTextInput/TypedFormTextInput';

import { type FormInputProps } from './types';
import { useFormInputChange } from './useFormInputChange';

type NumberInputProps = FormInputProps<number | null> & {
  label: string;
  adornment?: ReactNode;
};

const PARTIAL_NUMBER = /^-?\d*([.,]\d*)?$/;

const toDisplay = (value: number | null) =>
  value === null || value === undefined ? '' : String(value);

const NumberInput = (props: NumberInputProps) => {
  const { value, label, disabled, adornment } = props;
  const [display, setDisplay] = useState(toDisplay(value));
  const emit = useFormInputChange(props);

  useEffect(() => {
    const current = display === '' ? null : Number(display.replace(',', '.'));

    if (value !== current) {
      setDisplay(toDisplay(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync only on external value change, not while typing
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;

    if (raw !== '' && !PARTIAL_NUMBER.test(raw)) {
      return;
    }

    setDisplay(raw);

    if (raw === '') {
      emit(null);
      return;
    }

    const parsed = Number(raw.replace(',', '.'));

    if (!Number.isNaN(parsed)) {
      emit(parsed);
    }
  };

  return (
    <FormTextInput
      label={label}
      name={props.name ?? ''}
      value={display}
      handleChange={handleChange}
      disabled={disabled}
      endAdornment={
        adornment ? (
          <InputAdornment position="end">{adornment}</InputAdornment>
        ) : undefined
      }
    />
  );
};

export default NumberInput;
